import type { HeldCallItemProps } from './index.types';
import { ReactComponent as OnHoldIcon } from 'Assets/icons/on-hold.svg';
import { ReactComponent as SwitchIcon } from 'Assets/icons/switch.svg';
import { useSoftphoneContact } from 'Modules/softphone/hooks';
import React, { useCallback, useState } from 'react';
import { formatDuration } from 'Utils/formatDuration';
import { cn } from '~/lib/utils';
import { useCallDuration } from '../../hooks/useCallDuration';
import {
  baseClasses,
  itemClasses,
  itemHoverStateClasses,
} from '../../index.styles';

/**
 * A component that displays a single held call in the softphone interface.
 *
 * This component renders a button that shows information about a call that is currently on hold,
 * including the contact's display name, hold duration, and provides the ability to swap to this call.
 * The component supports keyboard navigation and displays different UI states based on hover/focus.
 */
export const HeldCallItem = React.memo(
  React.forwardRef<HTMLButtonElement, HeldCallItemProps>(
    (
      {
        call,
        onClick,
        showTimer,
        showSwapIcon = true,
        className = '',
        testID,
        isTriggerElement = false,
        ...htmlProps
      },
      ref
    ): React.ReactElement => {
      const [isHovered, setIsHovered] = useState(false);
      const durationStartTime = call.answerTime ?? call.startTime;
      const duration = useCallDuration(durationStartTime, call.duration);
      const { displayName, phoneNumber } = useSoftphoneContact(call.id, call);

      const finalDisplayName = displayName || phoneNumber || 'Unknown';

      const handleClick = useCallback(() => {
        if (!showSwapIcon) {
          return;
        }
        onClick?.(call.id);
      }, [call.id, onClick, showSwapIcon]);

      const isFocused = htmlProps['data-focused'] === true;
      const shouldShowSwitch = showSwapIcon && showTimer && (isHovered || isFocused);

      return (
        <button
          ref={ref}
          type="button"
          aria-label={
            showSwapIcon
              ? `${finalDisplayName} on hold for ${formatDuration(duration)}. Press Enter or Space to swap to this call`
              : `${finalDisplayName} on hold for ${formatDuration(duration)}`
          }
          title={showSwapIcon ? 'Click to swap call view (will hold current call)' : `${finalDisplayName} on hold`}
          className={cn(
            baseClasses,
            itemClasses,
            showSwapIcon && itemHoverStateClasses,
            !showSwapIcon && 'cursor-default',
            className
          )}
          onClick={handleClick}
          onMouseEnter={() => showSwapIcon && setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onKeyDown={(e) => {
            if (showSwapIcon && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault();
              handleClick();
            }
          }}
          {...htmlProps}
          data-testid={`${testID}-item-${
            showTimer ? 'with-timer' : 'without-timer'
          }`}
        >
          {/* Call Info - inline instead of separate component */}
          <div className="flex items-center gap-2 text-left flex-1 min-w-0">
            {isTriggerElement && (
              <OnHoldIcon className="size-[24px] text-foreground flex-shrink-0" />
            )}
            <div className="flex flex-col gap-[4px] min-w-0">
              {finalDisplayName && (
                <span className="text-[14px] text-foreground font-semibold truncate leading-[19px]">
                  {finalDisplayName}
                </span>
              )}
              {!!isTriggerElement && (
                <span className="text-xs text-foreground font-normal leading-[16px]">
                  on hold
                </span>
              )}
            </div>
          </div>

          {/* Right content - timer or swap icon */}
          <div className="flex-shrink-0">
            {shouldShowSwitch ? (
              <SwitchIcon className="size-[24px] text-foreground" />
            ) : showTimer ? (
              <span className="text-[14px] text-foreground leading-[19px] font-normal">
                {formatDuration(duration)}
              </span>
            ) : showSwapIcon ? (
              <SwitchIcon className="size-[24px] text-foreground" />
            ) : null}
          </div>
        </button>
      );
    }
  )
);

HeldCallItem.displayName = 'HeldCallItem';
